import { createEntityRecord } from '@kizen/api/records';
import { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toastVariant, useToast } from 'components/ToastProvider';
import useField from 'hooks/useField';
import { isReasonLostField, isStageField } from 'checks/fields';
import AxiosService, { getOriginalError } from 'services/AxiosService';
import Loader from 'components/Kizen/Loader';
import { useObjectDetails } from 'queries/models/custom-objects';
import BasicModalWithConfirmation from '../presets/BasicModalWithConfirmation';
import {
  CONTACT,
  PIPELINE,
  CUSTOM_OBJECT,
  dummyObject,
  toCategoryOption,
  notHidden,
  getToastConfigMessage,
  getObjectName,
  saveEntity,
  getPayload,
  getCreateEntityFormValues,
  PERMISSION_DENIED,
  UNARCHIVE_CONFLICT,
  UNARCHIVE_PROMPT,
  UNARCHIVE,
} from './helpers';
import { CategorySelector } from 'pages/Common/styles/NewEntityModal';
import {
  UnarchiveConflictModal,
  UnarchiveForbiddenModal,
} from '../UnarchiveModal';
import useFormValidation from 'hooks/useFormValidation';
import { useSelector } from 'react-redux';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { Fields } from './Fields';
import { getErrorMessage } from 'hooks/useErrors';
import { layers } from 'app/spacing';
import { useToggle } from 'react-use';
import { Button } from '@kizen/kds/Button';
import { isContact } from 'utility/fieldHelpers';
import { isPipelineObject } from '../utilities';

export { CONTACT, PIPELINE, CUSTOM_OBJECT };

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

const useDetails = (show, objectType, id) => {
  const clientObject = useSelector(getBusinessClientObject);
  const objectId = objectType === CONTACT ? clientObject.id : id;

  const { data: objectDetails = EMPTY_OBJECT, isLoading } = useObjectDetails(
    objectId,
    {
      enabled: show && !!objectId,
      refetchOnWindowFocus: false,
    }
  );

  const details = useMemo(
    () => ({
      ...objectDetails,
      ...(objectType !== CONTACT
        ? {
            access: {
              ...(objectDetails.access || {}),
              edit: objectDetails.entityAccess,
            },
          }
        : {}),
    }),
    [objectDetails, objectType]
  );

  return {
    details,
    isLoading,
  };
};

const CreateEntityModalFull = ({
  objectType,
  objectId,
  onCreated,
  onCancel,
  show,
  initialValues = EMPTY_OBJECT,
  handleUpdateFieldOption,
  defaultDirty = false,
  callRecordsEndpoint = false,
  isPreview = false,
}) => {
  const { t } = useTranslation();

  const [showToast] = useToast();
  const [dirty, setDirty] = useState(defaultDirty);
  const [isUnarchiveForbidden, setIsUnarchiveForbidden] = useState(false);
  const [isUnarchiveConflict, setIsUnarchiveConflict] = useState(false);
  const [unArchiveConflictMessage, setUnArchiveConflictMessage] = useState('');

  const toastMessage = useRef(getToastConfigMessage(objectType, t));

  const { details, isLoading } = useDetails(show, objectType, objectId);

  const { fields = EMPTY_ARRAY, fieldCategories = EMPTY_ARRAY } = details;

  const categoryOptions = useMemo(() => {
    if (fields && fieldCategories) {
      return [...fieldCategories]
        .sort(({ order: a }, { order: b }) => a - b)
        .map((c) =>
          toCategoryOption({
            ...c,
            fields: fields?.filter((field) => field.category === c.id),
          })
        );
    }
  }, [fields, fieldCategories]);

  const [selectedCategory, setSelectedCategory] = useField(
    () => (categoryOptions ? categoryOptions[0] : null),
    [show, categoryOptions]
  );

  // used for UI
  const selectedFields = useMemo(() => {
    const categoryFelds =
      (selectedCategory && selectedCategory.original.fields) || [];
    return categoryFelds
      .filter(notHidden)
      .sort((a, b) => parseInt(a.order, 10) - parseInt(b.order, 10));
  }, [selectedCategory]);

  const currentName = useMemo(
    () => getObjectName(details, objectType),
    [details, objectType]
  );

  const [values, setValues] = useField(() => {
    if (objectType === CONTACT && categoryOptions) {
      return getCreateEntityFormValues(
        { ...dummyObject, ...initialValues },
        fields
      );
    }

    if (!details) return {};

    const { name, owner, created, ...rest } = details;
    const reasonLostField = fields.find(isReasonLostField);
    const reasonLostFieldData = reasonLostField
      ? { ...reasonLostField, value: [] }
      : [];

    return getCreateEntityFormValues(
      { ...rest, ...initialValues, fields: [reasonLostFieldData] },
      fields
    );
  }, [details, show, categoryOptions]);

  const {
    validateFormState,
    validationProps,
    handleInputChange,
    setValidationState,
    firstFieldWithErrorRef,
  } = useFormValidation({
    setFormDirty: setDirty,
    formState: values,
    setFormState: setValues,
  });

  const handleClose = useCallback(() => {
    setValues({});
    onCancel?.();
    setDirty(false);
  }, [onCancel, setValues]);

  const handleChangeField = useCallback(
    (val, field, err) => {
      let reasonLostField;
      if (isStageField(field)) {
        const stage = field.options.find((opt) => opt.id === val);
        const stageDetails = details?.pipeline?.stages?.find(
          (s) => s.id === stage.id
        );
        if (stageDetails?.status !== 'lost') {
          reasonLostField = selectedFields.find(isReasonLostField);
        }
      }
      const relatedFields = reasonLostField
        ? {
            [reasonLostField.id]: [],
          }
        : {};
      handleInputChange(field.id, val, err, relatedFields);
    },
    [selectedFields, details, handleInputChange]
  );

  const handleSubmit = useCallback(
    async (unarchive = UNARCHIVE_PROMPT) => {
      try {
        if (callRecordsEndpoint) {
          const valuesForPayload = fields.reduce((acc, field) => {
            if (field.id in values && field.access.edit) {
              acc[field.id] = values[field.id];
            }
            return acc;
          }, {});
          const payload = getPayload(valuesForPayload);

          const data = await createEntityRecord(AxiosService, objectId, {
            fields: payload,
            unarchive,
          });

          await onCreated(data);
        } else {
          const newRecord = await saveEntity(
            objectType,
            values,
            fields,
            unarchive,
            objectId,
            true
          );
          await onCreated(newRecord);
        }
        showToast(
          unarchive === UNARCHIVE
            ? {
                variant: toastVariant.SUCCESS,
                message: `${t(`EntityName was successfully unarchived`, {
                  entityName: currentName,
                })}`,
              }
            : toastMessage.current.success
        );
        handleClose();
      } catch (error) {
        const orig = getOriginalError(error);
        if (orig) {
          if (orig.code === PERMISSION_DENIED) {
            setIsUnarchiveForbidden(true);
            setUnArchiveConflictMessage(orig.detail);
          }
          if (orig.code === UNARCHIVE_CONFLICT) {
            setIsUnarchiveConflict(true);
            setUnArchiveConflictMessage(orig.detail);
          }
          // special error status
          if (orig.non_field_errors) {
            showToast({
              ...toastMessage.current.error,
              message: orig.non_field_errors.toString(),
            });
          } else {
            firstFieldWithErrorRef.current = null;

            const requestPayload = JSON.parse(error.config?.data || '{}');

            const errState = orig.fields
              ? orig.fields.reduce((acc, item, i) => {
                  const message = getErrorMessage(item);
                  const fieldId = requestPayload.fields?.[i]?.id;
                  if (message && fieldId) {
                    if (!firstFieldWithErrorRef.current) {
                      firstFieldWithErrorRef.current = fieldId;
                    }
                    acc[fieldId] = message;
                  }
                  return acc;
                }, {})
              : Object.keys(orig).reduce((acc, item) => {
                  const f = fields.find((i) => item === i.name);
                  if (f) {
                    if (!firstFieldWithErrorRef.current) {
                      firstFieldWithErrorRef.current = f.id;
                    }
                    acc[f.id] = getErrorMessage(orig[item]);
                  }
                  return acc;
                }, {});

            const categoryWithRequiredFields = categoryOptions.find((item) => {
              return item.original.fields.some(
                (field) => field.id === firstFieldWithErrorRef.current
              );
            });
            setSelectedCategory((prev) => categoryWithRequiredFields || prev);
            setValidationState(errState);
          }
        } else {
          // rest unhandled cases
          showToast(toastMessage.current.error);
        }
      }
    },
    [
      callRecordsEndpoint,
      fields,
      categoryOptions,
      currentName,
      handleClose,
      objectId,
      objectType,
      onCreated,
      setSelectedCategory,
      showToast,
      values,
      t,
      firstFieldWithErrorRef,
      setValidationState,
    ]
  );

  const handleUnarchive = useCallback(
    (unarchive) => {
      handleSubmit(unarchive);
      setIsUnarchiveConflict(false);
      setUnArchiveConflictMessage('');
    },
    [handleSubmit]
  );

  return (
    <BasicModalWithConfirmation
      dirty={!isPreview && dirty}
      className={`no-drag`}
      show={!!objectType && show}
      onHide={handleClose}
      onConfirm={
        isPreview
          ? handleClose
          : async () => {
              if (!validateFormState(t)) {
                const field = fields.find(
                  (f) => f.id === firstFieldWithErrorRef.current
                );
                const categoryOption = categoryOptions.find(
                  (opt) => opt.value === field.category
                );
                setSelectedCategory(categoryOption);
                return;
              }
              await handleSubmit();
            }
      }
      heading={t('{{addMode}} {{entityName}}', {
        addMode: isPreview ? t('Previewing Add') : t('Add'),
        entityName: objectType === CONTACT ? t(`Contact`) : details?.entityName,
      })}
      buttonText={isPreview ? t('Close') : t('Save')}
      leftBtn={isPreview ? null : undefined}
      actionBtnColor={isPreview ? 'blue' : 'green'}
      defaultLeftBtnText={t('Cancel')}
      hasOneBtnSubmit
      showLoadingIndicator
      enforceFocus={false}
    >
      <Loader loading={isLoading}>
        <UnarchiveForbiddenModal
          show={isUnarchiveForbidden}
          onConfirm={() => {
            setIsUnarchiveForbidden(false);
            setUnArchiveConflictMessage('');
          }}
          onHide={() => {
            setIsUnarchiveForbidden(false);
            setUnArchiveConflictMessage('');
          }}
          message={unArchiveConflictMessage}
        />
        <UnarchiveConflictModal
          show={isUnarchiveConflict}
          onConfirm={() => handleUnarchive(UNARCHIVE)}
          onAdditionalConfirm={() => handleUnarchive('overwrite')}
          onHide={() => {
            setIsUnarchiveConflict(false);
            setUnArchiveConflictMessage('');
          }}
          message={unArchiveConflictMessage}
        />
        <CategorySelector
          small
          portal
          classNamePrefix="CategorySelector"
          options={categoryOptions}
          value={selectedCategory}
          onChange={setSelectedCategory}
          objectId={objectId}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: layers.popoverInModal,
              marginTop: '-10px',
            }),
          }}
        />
        <Fields
          key={selectedCategory?.value}
          selectedFields={selectedFields}
          handleChangeField={handleChangeField}
          validationProps={validationProps}
          handleUpdateFieldOption={handleUpdateFieldOption}
          details={details}
          objectType={objectType}
          values={values}
          objectId={objectId}
        />
      </Loader>
    </BasicModalWithConfirmation>
  );
};

export const PreviewCreateEntityModalButton = ({ model, className }) => {
  const { t } = useTranslation();
  const [show, setShow] = useToggle(false);

  return (
    <>
      <Button
        className={className}
        color="secondary"
        variant="text"
        onClick={setShow}
      >
        {t('Preview')}
      </Button>
      {show && (
        <CreateEntityModalFull
          objectType={
            isContact(model)
              ? CONTACT
              : isPipelineObject(model)
                ? PIPELINE
                : CUSTOM_OBJECT
          }
          objectId={model.id}
          show
          isPreview
          onCancel={setShow}
        />
      )}
    </>
  );
};

export default CreateEntityModalFull;
